import React, { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Heart, Calendar, Coins, ClipboardList, Search, ChevronRight, Trash2, Mail, Briefcase, Camera, Zap, UserCheck, UserX, Send, Home as HomeIcon, Target } from "lucide-react";

const TABS = [
  { k: "users",        label: "Gebruikers",       icon: UserCheck,     endpoint: "/admin/users",               titleKey: "name",           deleteKind: null,                    special: "users" },
  { k: "properties",   label: "Woningaanmeldingen",icon: HomeIcon,     endpoint: "/admin/properties",          titleKey: "title",          deleteKind: "properties" },
  { k: "buyer-profiles",label:"Zoekprofielen",    icon: Target,        endpoint: "/admin/buyers",              titleKey: "name",           deleteKind: "buyer-profiles" },
  { k: "interests",    label: "Interesses",       icon: Heart,         endpoint: "/admin/interests",           titleKey: "property_title", deleteKind: "interests" },
  { k: "viewings",     label: "Bezichtigingen",   icon: Calendar,      endpoint: "/admin/viewings",            titleKey: "property_title", deleteKind: "viewings" },
  { k: "offers",       label: "Biedingen",        icon: Coins,         endpoint: "/admin/offers",              titleKey: "property_title", deleteKind: "offers" },
  { k: "zoekservice",  label: "Zoekservice",      icon: Search,        endpoint: "/admin/zoekservice/orders",  titleKey: "package_label",  deleteKind: "zoekservice-orders" },
  { k: "spoed",        label: "Spoed",            icon: Zap,           endpoint: "/admin/spoed-orders",        titleKey: "package_label",  deleteKind: "spoed-orders" },
  { k: "photography",  label: "Fotografie",       icon: Camera,        endpoint: "/admin/photography/bookings",titleKey: "package",        deleteKind: "photography-bookings" },
  { k: "contact",      label: "Contact-berichten",icon: Mail,          endpoint: "/admin/contact-messages",    titleKey: "subject",        deleteKind: "contact-messages" },
  { k: "careers",      label: "Sollicitaties",    icon: Briefcase,     endpoint: "/admin/careers",             titleKey: "role_label",     deleteKind: "careers" },
  { k: "tasks",        label: "Taken",            icon: ClipboardList, endpoint: "/admin/tasks",               titleKey: "title",          deleteKind: "tasks" },
  { k: "complaints",   label: "Klachten",         icon: Users,         endpoint: "/admin/complaints",          titleKey: "subject",        deleteKind: "complaints" },
];

const STATUS_ACTIONS = {
  interests: ["contacted", "viewing_scheduled", "converted_to_offer", "not_interested", "closed"],
  viewings: ["confirmed", "rescheduled", "completed", "no_show", "cancelled"],
  offers: ["communicated_to_seller", "counter_offer", "accepted", "rejected", "withdrawn", "sold"],
  complaints: ["in_review", "in_progress", "resolved", "closed", "rejected"],
  careers: ["reviewing", "contacted", "interview", "hired", "rejected", "archived"],
  photography: ["quoted", "accepted", "scheduled", "photographer_assigned", "completed", "photos_uploaded", "approved", "cancelled"],
};

const eur = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const CONTINGENCY_LABELS = { bouwkundige_keuring: "Bouwkundige keuring", taxatie: "Taxatie", verkoop_eigen_woning: "Verkoop eigen woning", financiering: "Financiering", nvm_akte: "NVM koopakte" };
const OF_LABELS = { submitted: "Ingediend", communicated_to_seller: "Doorgegeven", counter_offer: "Tegenbod", accepted: "Geaccepteerd", rejected: "Afgewezen", withdrawn: "Ingetrokken", sold: "Verkocht" };

export default function AdminCRM() {
  const [tab, setTab] = useState("users");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [userFilter, setUserFilter] = useState("pending");
  const [userAction, setUserAction] = useState(null); // {kind: "approve"|"reject"|"message", user}
  const [msgSubject, setMsgSubject] = useState("");
  const [msgBody, setMsgBody] = useState("");

  const cfg = TABS.find((t) => t.k === tab);

  const load = async () => {
    setBusy(true);
    try {
      const params = tab === "users" && userFilter !== "all" ? { status: userFilter } : {};
      const { data } = await http.get(cfg.endpoint, { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, userFilter]);

  const patchStatusEndpoint = () => {
    // maps: /admin/foo -> /admin/foo/{id}/status  BUT some paths are different
    switch (tab) {
      case "photography": return "/admin/photography/bookings/{id}/status";
      case "zoekservice": return null; // no status update for now
      case "spoed":       return null;
      case "contact":     return null;
      case "tasks":       return "/admin/tasks/{id}/complete";
      default:            return cfg.endpoint + "/{id}/status";
    }
  };

  const setStatus = async (id, status) => {
    const tmpl = patchStatusEndpoint();
    if (!tmpl) return;
    const url = tmpl.replace("{id}", id);
    try {
      if (tab === "tasks") await http.patch(url);
      else await http.patch(url, { status, admin_note: note || null });
      toast.success("Bijgewerkt"); setNote(""); setSelected(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Weet u zeker dat u dit item wilt verwijderen?")) return;
    try {
      if (tab === "users") {
        await http.delete(`/admin/users/${id}`);
      } else {
        await http.delete(`/admin/requests/${cfg.deleteKind}/${id}`);
      }
      toast.success("Verwijderd");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const purgeAll = async () => {
    if (tab === "users") { toast.error("Purge is niet beschikbaar voor gebruikers"); return; }
    try {
      const { data } = await http.post(`/admin/requests/${cfg.deleteKind}/purge`);
      toast.success(`${data.deleted} items verwijderd`);
      setConfirmPurge(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitUserAction = async () => {
    if (!userAction) return;
    const { kind, user } = userAction;
    try {
      if (kind === "approve") {
        await http.patch(`/admin/users/${user.id}/approve`, { welcome_message: msgBody || null });
        toast.success("Gebruiker goedgekeurd — bevestigingsmail verzonden");
      } else if (kind === "reject") {
        await http.patch(`/admin/users/${user.id}/reject`, { welcome_message: msgBody || null });
        toast.success("Gebruiker afgewezen — mail verzonden");
      } else if (kind === "message") {
        if (!msgSubject || !msgBody) { toast.error("Onderwerp en bericht zijn verplicht"); return; }
        await http.post(`/admin/users/${user.id}/message`, { subject: msgSubject, message: msgBody });
        toast.success("Mail verzonden");
      }
      setUserAction(null); setMsgSubject(""); setMsgBody(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">Operations</p>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2">CRM & Aanvragen</h1>
          <p className="mt-2 text-sm text-slate-500">Alle aanvragen die via het portaal binnenkomen staan hier centraal. U kunt items openen, van status wijzigen en verwijderen (bijv. testdata).</p>
        </div>
        <a href="/admin" className="wv-btn-outline text-sm" data-testid="crm-back-admin">← Admin dashboard</a>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-2 flex-wrap border-b border-slate-200 pb-2 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => { setTab(t.k); setConfirmPurge(false); }}
            className={`px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${tab === t.k ? "bg-[color:var(--wv-navy)] text-white" : "text-slate-600 hover:bg-slate-50"}`}
            data-testid={`crm-tab-${t.k}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Toolbar: purge test data + user filter */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <p className="text-sm text-slate-500">Totaal: <span className="font-mono-num font-bold text-slate-800">{items.length}</span></p>
        {tab === "users" && (
          <div className="flex items-center gap-1 flex-wrap">
            {[["pending","In afwachting"],["approved","Goedgekeurd"],["rejected","Afgewezen"],["all","Alle"]].map(([k,label]) => (
              <button key={k} onClick={() => setUserFilter(k)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${userFilter === k ? "bg-[color:var(--wv-green)] text-white" : "text-slate-600 hover:bg-slate-100 border border-slate-200"}`}
                data-testid={`crm-users-filter-${k}`}>{label}</button>
            ))}
          </div>
        )}
        {tab !== "users" && (!confirmPurge ? (
          <button onClick={() => setConfirmPurge(true)}
            className="wv-btn-outline text-xs text-red-700 border-red-200 hover:bg-red-50 inline-flex items-center gap-1"
            data-testid={`crm-purge-btn-${tab}`} disabled={items.length === 0}>
            <Trash2 className="w-3.5 h-3.5" /> Verwijder alle {cfg.label.toLowerCase()}
          </button>
        ) : (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-red-700 font-semibold">Zeker weten? Dit verwijdert {items.length} item(s) permanent.</span>
            <button onClick={purgeAll} className="wv-btn-primary text-xs bg-red-600 hover:bg-red-700 border-red-600" data-testid={`crm-purge-confirm-${tab}`}>Ja, verwijder alles</button>
            <button onClick={() => setConfirmPurge(false)} className="wv-btn-outline text-xs" data-testid={`crm-purge-cancel-${tab}`}>Annuleren</button>
          </div>
        ))}
      </div>

      {busy && <p className="mt-6 text-slate-500">Laden…</p>}

      {/* Users table (special layout) */}
      {!busy && tab === "users" && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="p-3 font-semibold">Naam</th>
                <th className="p-3 font-semibold">Email</th>
                <th className="p-3 font-semibold hidden md:table-cell">Telefoon</th>
                <th className="p-3 font-semibold hidden lg:table-cell">Land</th>
                <th className="p-3 font-semibold">Rol</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`crm-user-row-${u.id}`}>
                  <td className="p-3 font-semibold">{u.name}</td>
                  <td className="p-3 text-xs">{u.email}</td>
                  <td className="p-3 hidden md:table-cell text-xs font-mono-num">{u.phone || "—"}</td>
                  <td className="p-3 hidden lg:table-cell text-xs">{u.country || "—"}</td>
                  <td className="p-3 text-xs uppercase font-semibold">{u.role}</td>
                  <td className="p-3">
                    <span className={`wv-status-pill ${u.approval_status === "approved" ? "wv-status-published" : u.approval_status === "rejected" ? "wv-status-rejected" : "wv-status-review"}`}>
                      {u.approval_status || "—"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                      {u.approval_status !== "approved" && (
                        <button onClick={() => { setUserAction({ kind: "approve", user: u }); setMsgBody(""); }}
                          className="wv-btn-green text-xs inline-flex items-center gap-1"
                          data-testid={`crm-user-approve-${u.id}`}><UserCheck className="w-3 h-3" /> Goedkeuren</button>
                      )}
                      {u.approval_status !== "rejected" && u.role !== "admin" && (
                        <button onClick={() => { setUserAction({ kind: "reject", user: u }); setMsgBody(""); }}
                          className="wv-btn-outline text-xs text-red-700 border-red-200 hover:bg-red-50 inline-flex items-center gap-1"
                          data-testid={`crm-user-reject-${u.id}`}><UserX className="w-3 h-3" /> Afwijzen</button>
                      )}
                      <button onClick={() => { setUserAction({ kind: "message", user: u }); setMsgSubject(""); setMsgBody(""); }}
                        className="wv-btn-outline text-xs inline-flex items-center gap-1"
                        data-testid={`crm-user-msg-${u.id}`}><Send className="w-3 h-3" /> Bericht</button>
                      {u.role !== "admin" && (
                        <button onClick={() => deleteItem(u.id)} className="wv-btn-outline text-xs text-red-700 border-red-200 hover:bg-red-50" data-testid={`crm-user-del-${u.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-slate-500">Geen gebruikers in filter "{userFilter}".</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {!busy && tab !== "users" && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="p-3 font-semibold">Onderwerp</th>
                <th className="p-3 font-semibold hidden md:table-cell">Van</th>
                <th className="p-3 font-semibold hidden md:table-cell">Datum</th>
                {tab === "offers" && <th className="p-3 font-semibold">Bod</th>}
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`crm-row-${it.id}`}>
                  <td className="p-3">
                    <p className="font-semibold">{it[cfg.titleKey] || it.title || it.subject || it.name || "—"}</p>
                    {it.property_title && <p className="text-xs text-slate-500">{it.property_title}</p>}
                    {it.role_label && !it[cfg.titleKey] && <p className="text-xs text-slate-500">{it.role_label}</p>}
                  </td>
                  <td className="p-3 hidden md:table-cell text-xs">{it.name || it.email || it.created_by || "—"}<br /><span className="text-slate-400">{it.email}</span></td>
                  <td className="p-3 hidden md:table-cell font-mono-num text-xs">{it.created_at ? new Date(it.created_at).toLocaleDateString("nl-NL") : "—"}</td>
                  {tab === "offers" && <td className="p-3 font-mono-num font-bold">{eur(it.amount || 0)}</td>}
                  <td className="p-3"><span className="wv-status-pill wv-status-review">{it.status || "—"}</span></td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => setSelected(it)} className="wv-btn-outline text-xs inline-flex items-center gap-1" data-testid={`crm-open-${it.id}`}>Open <ChevronRight className="w-3 h-3" /></button>
                      <button onClick={() => deleteItem(it.id)} className="wv-btn-outline text-xs text-red-700 border-red-200 hover:bg-red-50 inline-flex items-center gap-1" data-testid={`crm-delete-${it.id}`} title="Verwijderen">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-500">Geen items in {cfg.label.toLowerCase()}.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setSelected(null)}>
          <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-white rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="crm-modal">
            <div className="p-6 border-b border-slate-100">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">{cfg.label}</p>
              <h2 className="font-display text-2xl font-extrabold">{selected[cfg.titleKey] || selected.title || selected.subject || selected.name}</h2>
              {selected.property_title && <p className="text-sm text-slate-600 mt-1">Woning: {selected.property_title}</p>}
              {selected.status && <p className="text-sm text-slate-600 mt-1">Status: <span className="font-semibold">{selected.status}</span></p>}
            </div>
            <div className="p-6 space-y-3">
              <dl className="grid sm:grid-cols-2 gap-2 text-sm">
                {selected.name && <div><dt className="text-slate-500">Naam</dt><dd className="font-semibold">{selected.name}</dd></div>}
                {selected.email && <div><dt className="text-slate-500">Email</dt><dd className="font-semibold">{selected.email}</dd></div>}
                {selected.phone && <div><dt className="text-slate-500">Telefoon</dt><dd className="font-semibold">{selected.phone}</dd></div>}
                {selected.amount && <div><dt className="text-slate-500">Bod</dt><dd className="font-mono-num font-bold">{eur(selected.amount)}</dd></div>}
                {selected.asking_price && <div><dt className="text-slate-500">Vraagprijs</dt><dd className="font-mono-num">{eur(selected.asking_price)}</dd></div>}
                {selected.financing && <div><dt className="text-slate-500">Financiering</dt><dd className="font-semibold">{selected.financing} {selected.financing_reservation ? "(met voorbehoud)" : "(zonder voorbehoud)"}</dd></div>}
                {selected.preferred_date && <div><dt className="text-slate-500">Voorkeur datum</dt><dd className="font-semibold">{selected.preferred_date} · {selected.preferred_time}</dd></div>}
                {selected.package_label && <div><dt className="text-slate-500">Pakket</dt><dd className="font-semibold">{selected.package_label} — € {selected.package_price}</dd></div>}
                {selected.category && <div><dt className="text-slate-500">Categorie</dt><dd className="font-semibold">{selected.category}</dd></div>}
                {selected.role_label && <div><dt className="text-slate-500">Rol</dt><dd className="font-semibold">{selected.role_label}</dd></div>}
                {selected.package && !selected.package_label && <div><dt className="text-slate-500">Pakket</dt><dd className="font-semibold">{selected.package}</dd></div>}
              </dl>
              {(selected.message || selected.description || selected.notes || selected.conditions || selected.message_to_seller || selected.motivation || selected.text) && (
                <p className="text-sm text-slate-700 p-3 bg-slate-50 rounded-lg whitespace-pre-wrap">{selected.message_to_seller || selected.message || selected.description || selected.notes || selected.conditions || selected.motivation || selected.text}</p>
              )}
              {tab === "offers" && (
                <div className="p-4 rounded-xl bg-[color:var(--wv-sand)] border border-slate-200">
                  <p className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Bod details</p>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    <div>Financiering: <span className="font-semibold">{selected.financing}</span>{selected.financing_reservation ? ` · voorbehoud ${selected.financing_reservation_days || 42} dgn` : " · geen voorbehoud"}</div>
                    {selected.deposit_percent != null && <div>Waarborgsom: <span className="font-mono-num font-semibold">{selected.deposit_percent}%</span></div>}
                    {selected.transfer_date && <div>Overdracht: <span className="font-semibold">{selected.transfer_date}</span></div>}
                    {selected.valid_until && <div>Geldig tot: <span className="font-semibold">{selected.valid_until}</span></div>}
                    {selected.notary_choice && <div>Notaris: <span className="font-semibold">{selected.notary_choice.replace(/_/g, " ")}</span></div>}
                    {selected.contingency_deadline_days && <div>Voorwaarden-termijn: <span className="font-mono-num font-semibold">{selected.contingency_deadline_days} dgn</span></div>}
                  </div>
                  {selected.contingencies?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-slate-500">Ontbindende voorwaarden</p>
                      <ul className="mt-1 list-disc ml-5 text-sm">{selected.contingencies.map((c) => <li key={c}>{CONTINGENCY_LABELS[c] || c}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}
              {tab === "offers" && selected.activities?.length > 0 && (
                <div className="p-4 rounded-xl bg-white border border-slate-200">
                  <p className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Activiteit tijdlijn</p>
                  <ul className="space-y-1.5">
                    {selected.activities.slice().reverse().map((a, i) => (
                      <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--wv-green)] mt-1.5 flex-shrink-0" />
                        <div><span className="font-mono-num text-slate-400">{new Date(a.at).toLocaleString("nl-NL")}</span> — <span className="font-semibold text-slate-800">{OF_LABELS[a.new_status] || a.new_status}</span>{a.by ? ` · ${a.by}` : ""}{a.note ? ` · ${a.note}` : ""}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {STATUS_ACTIONS[tab] && (
                <>
                  <div>
                    <label className="text-sm font-semibold">Notitie (optioneel)</label>
                    <textarea className="wv-input mt-1" rows="2" value={note} onChange={(e) => setNote(e.target.value)} data-testid="crm-note" />
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                    {STATUS_ACTIONS[tab].map((s) => (
                      <button key={s} onClick={() => setStatus(selected.id, s)} className="wv-btn-outline text-xs" data-testid={`crm-status-${s}`}>{s.replace(/_/g, " ")}</button>
                    ))}
                  </div>
                </>
              )}
              {tab === "tasks" && selected.status !== "done" && (
                <button onClick={() => setStatus(selected.id, "done")} className="wv-btn-green text-sm" data-testid="crm-task-complete">Markeer als voltooid</button>
              )}

              {/* Delete inside modal */}
              <div className="pt-3 border-t border-slate-100">
                <button onClick={() => deleteItem(selected.id)} className="wv-btn-outline text-xs text-red-700 border-red-200 hover:bg-red-50 inline-flex items-center gap-1" data-testid={`crm-modal-delete-${selected.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Item permanent verwijderen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* User action modal */}
      {userAction && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setUserAction(null)}>
          <div className="max-w-lg w-full bg-white rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="crm-user-modal">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500">
              {userAction.kind === "approve" && "Gebruiker goedkeuren"}
              {userAction.kind === "reject" && "Gebruiker afwijzen"}
              {userAction.kind === "message" && "Bericht sturen"}
            </p>
            <h2 className="font-display text-2xl font-extrabold mt-1">{userAction.user.name}</h2>
            <p className="text-sm text-slate-600 mt-1">{userAction.user.email}</p>

            {userAction.kind === "approve" && (
              <>
                <p className="mt-4 text-sm text-slate-700">Er wordt automatisch een bevestigingsmail gestuurd zodra u goedkeurt. Voeg optioneel een persoonlijk welkomstbericht toe:</p>
                <textarea className="wv-input mt-3" rows="4" placeholder="Optioneel — persoonlijk welkomstbericht"
                  value={msgBody} onChange={(e) => setMsgBody(e.target.value)} data-testid="crm-user-welcome" />
              </>
            )}
            {userAction.kind === "reject" && (
              <>
                <p className="mt-4 text-sm text-slate-700">De gebruiker ontvangt een e-mail dat de aanvraag niet is goedgekeurd. Voeg optioneel een reden toe:</p>
                <textarea className="wv-input mt-3" rows="4" placeholder="Optioneel — reden voor afwijzing"
                  value={msgBody} onChange={(e) => setMsgBody(e.target.value)} data-testid="crm-user-reject-reason" />
              </>
            )}
            {userAction.kind === "message" && (
              <>
                <label className="text-sm font-semibold mt-4 block">Onderwerp</label>
                <input className="wv-input mt-1" value={msgSubject} onChange={(e) => setMsgSubject(e.target.value)} data-testid="crm-user-msg-subject" />
                <label className="text-sm font-semibold mt-3 block">Bericht</label>
                <textarea className="wv-input mt-1" rows="6" value={msgBody} onChange={(e) => setMsgBody(e.target.value)} data-testid="crm-user-msg-body" />
              </>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setUserAction(null)} className="wv-btn-outline text-sm" data-testid="crm-user-cancel">Annuleren</button>
              <button onClick={submitUserAction}
                className={`text-sm ${userAction.kind === "reject" ? "wv-btn-primary bg-red-600 hover:bg-red-700 border-red-600" : "wv-btn-green"}`}
                data-testid="crm-user-confirm">
                {userAction.kind === "approve" && "Goedkeuren & mail verzenden"}
                {userAction.kind === "reject" && "Afwijzen & mail verzenden"}
                {userAction.kind === "message" && "Verzenden"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
